import type { SystemStyleObject } from '@chakra-ui/react';
import { AbsoluteCenter, ProgressCircle as ChakraProgressCircle } from '@chakra-ui/react';
import * as React from 'react';

import {
  progressCircleDefaultSize,
  progressCircleRangeColor,
  progressCircleThickness,
  progressCircleTrackColor,
} from './progress-circle.kds';

const clampPercentage = (value: number | null | undefined, max: number | null | undefined): number => {
  if (value == null || max == null || max <= 0) return 0;
  if (Number.isNaN(value) || Number.isNaN(max)) return 0;
  const pct = (value / max) * 100;
  return Number.isNaN(pct) ? 0 : Math.min(Math.max(pct, 0), 100);
};

export interface ProgressCircleProps extends Omit<ChakraProgressCircle.RootProps, 'size'> {
  /** Chakra size preset. Defaults to `sm` (32px). */
  size?: NonNullable<ChakraProgressCircle.RootProps['size']>;
  /** Maximum value used to compute the percentage. Default `100`. */
  max?: number;
  /** Track (background) stroke color. Defaults to `neutral.100`. */
  trackColor?: SystemStyleObject['stroke'];
  /** Range (filled) stroke color. Defaults to `brand.500`. */
  color?: SystemStyleObject['stroke'];
  /** Content rendered centered inside the circle (e.g. value text). */
  children?: React.ReactNode;
}

export const ProgressCircle = React.forwardRef<HTMLDivElement, ProgressCircleProps>(
  function ProgressCircle(props, ref) {
    const {
      value,
      max = 100,
      size = progressCircleDefaultSize,
      trackColor = progressCircleTrackColor,
      color = progressCircleRangeColor,
      children,
      ...rest
    } = props;

    const isIndeterminate = value == null;
    const numericValue = typeof value === 'number' ? value : Number(value);
    const numericMax = typeof max === 'number' ? max : Number(max);
    const sanitized = isIndeterminate ? null : clampPercentage(numericValue, numericMax);

    return (
      <ChakraProgressCircle.Root ref={ref} size={size} value={sanitized} max={100} {...rest}>
        <ChakraProgressCircle.Circle css={{ '--thickness': progressCircleThickness }}>
          <ChakraProgressCircle.Track stroke={trackColor} />
          <ChakraProgressCircle.Range stroke={color} strokeLinecap="butt" />
        </ChakraProgressCircle.Circle>
        {children != null && <AbsoluteCenter>{children}</AbsoluteCenter>}
      </ChakraProgressCircle.Root>
    );
  }
);
